<script>
import { isValidApPassword, WIFI_AP_PASSWORD_MIN_LENGTH, WIFI_AP_PASSWORD_MAX_LENGTH } from '../js/wifiValidation.js';

export default {
  name: 'WifiForm',

  components: {},

  props: {
    settings: {
      type: Object,
      required: true,
    },

    // Current WiFi status from the device (GET_WIFI_STATUS).
    // Shape: { connected: bool, ip: string|null, ssid: string|null, mode: string|null, hostname: string|null }
    // null means status is unavailable (e.g. HTTP connection, or not yet fetched).
    wifiStatus: {
      type: Object,
      default: null,
    },

    // DeviceConnection instance.  When provided and it supports wifiReconnect()
    // (USB only), the "Reconnect" button is shown below the station password.
    connectedDevice: {
      type: Object,
      default: null,
    },

    // 'wifi' | 'broadcast' | null (show both)
    section: {
      type: String,
      default: null,
    },
  },

  emits: ['update:wifiStatus'],

  data() {
    return {
      showStaPassword: false,
      showApPassword: false,
      reconnecting: false,
      // null = idle, 'failed' = connected:false returned, 'error' = threw
      reconnectResult: null,
    };
  },

  computed: {
    // The device redacts the stored station password (MDL-360) and sends a
    // boolean in its place, so an empty field no longer means "no password".
    staPasswordStored() {
      return this.settings.wifi_sta_password_set === true
        && this.settings.wifi_sta_password === undefined;
    },

    timeoutInvalid() {
      const t = Number(this.settings.wifi_connect_timeout_s ?? 10);
      return !Number.isInteger(t) || t < 5 || t > 60;
    },

    apPasswordInvalid() {
      return !isValidApPassword(this.settings.wifi_ap_password);
    },

    apPasswordMinLength() {
      return WIFI_AP_PASSWORD_MIN_LENGTH;
    },

    apPasswordMaxLength() {
      return WIFI_AP_PASSWORD_MAX_LENGTH;
    },

    noWifiWarning() {
      return this.settings.wifi_ap_disabled && !(this.settings.wifi_sta_ssid ?? '').trim();
    },

    // Show the reconnect button only when the device supports the command
    // (i.e. wifiReconnect returns non-null, which rules out HTTP connections).
    canReconnect() {
      return this.connectedDevice !== null &&
        typeof this.connectedDevice.wifiReconnect === 'function';
    },
  },

  methods: {
    // Normalise text inputs so that an empty/whitespace-only value is stored as
    // null (consistent with the device's config schema) rather than "".
    setStrField(key, value) {
      this.settings[key] = value.trim() || null;
    },

    async reconnect() {
      if (this.reconnecting) return;
      this.reconnecting = true;
      this.reconnectResult = null;
      try {
        const status = await this.connectedDevice.wifiReconnect(
          this.settings.wifi_sta_ssid ?? '',
          // The device no longer sends the stored password back, so the field
          // is undefined until the user types one. null asks the device to
          // reuse what it has; '' would mean "this network is open".
          this.settings.wifi_sta_password ?? null,
        );
        if (status && status.connected) {
          this.$emit('update:wifiStatus', status);
          this.reconnectResult = null;
        } else {
          this.reconnectResult = 'failed';
        }
      } catch {
        this.reconnectResult = 'error';
      } finally {
        this.reconnecting = false;
      }
    },
  },
};
</script>

<template>
  <div class="p-4">
    <h2 class="text-2xl font-semibold">{{ section === 'broadcast' ? 'Broadcast Mode' : 'WiFi Settings' }}</h2>
    <p class="text-gray-500 dark:text-gray-100/50">
      <template v-if="section === 'broadcast'">
        In broadcast mode Midulate creates its own WiFi network for you to join.
        This is useful for initial setup, or if there is no WiFi network available.
        Do not leave it open without a password.
        When you join it, use the built-in editor on the device.
      </template>
      <template v-else>
        Configure WiFi connectivity for Midulate. Changes take effect after saving and rebooting the device.
      </template>
    </p>

    <div :class="['grid gap-4', !section ? 'sm:grid-cols-2' : '']">
      <div v-if="!section || section === 'wifi'">
      <!-- ── Current status ───────────────────────────────────────── -->
      <div v-if="wifiStatus !== null" class="card bg-base-100 shadow-sm border border-base-200 mt-4">
        <div class="card-body p-5">
          <h3 class="font-semibold text-sm uppercase tracking-wider text-base-content/50 mb-3">Current Status</h3>
          <div class="flex flex-wrap gap-y-3 text-sm divide-x divide-base-200">
            <div class="pr-6">
              <p class="text-base-content/50 mb-1">Connected</p>
              <span v-if="wifiStatus.connected" class="badge badge-sm badge-success">Yes</span>
              <span v-else class="badge badge-sm badge-ghost">No</span>
            </div>
            <div v-if="wifiStatus.ssid" class="px-6">
              <p class="text-base-content/50 mb-1">Network</p>
              <span class="font-mono">{{ wifiStatus.ssid }}</span>
            </div>
            <div v-if="wifiStatus.ip" class="px-6">
              <p class="text-base-content/50 mb-1">IP Address</p>
              <span class="font-mono">{{ wifiStatus.ip }}</span>
            </div>
            <div v-if="wifiStatus.hostname" class="px-6">
              <p class="text-base-content/50 mb-1">Hostname</p>
              <span class="font-mono">{{ wifiStatus.hostname }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Station mode ─────────────────────────────────────────── -->
      <h3 v-if="!section" class="text-xl font-semibold mt-4 pt-2 pb-2">Connect to Wifi Network</h3>

      <p v-if="!section" class="text-gray-500 dark:text-gray-100/50 mb-4">
        To connect to a WiFi network, enter the network's SSID and password below.
        This will allow you to connect to this editor over WiFi.
      </p>

      <!-- Station SSID -->
      <fieldset class="fieldset mt-4 mb-2">
        <legend class="fieldset-legend">SSID</legend>
        <input
          type="text"
          :value="settings.wifi_sta_ssid ?? ''"
          @input="setStrField('wifi_sta_ssid', $event.target.value)"
          placeholder="Home network name"
          class="input input-bordered"
        />
        <p class="label-text-alt text-xs">The WiFi network for Midulate to join. Leave blank to stay in broadcast mode.</p>
      </fieldset>

      <!-- Station Password -->
      <fieldset class="fieldset mb-2">
        <legend class="fieldset-legend">Password</legend>
        <label class="input">
          <input
            :type="showStaPassword ? 'text' : 'password'"
            :value="settings.wifi_sta_password ?? ''"
            @input="setStrField('wifi_sta_password', $event.target.value)"
            :placeholder="staPasswordStored ? 'Unchanged — type to replace' : 'Leave blank for open network'"
            class="input input-bordered"
          />
          <button
            type="button"
            class="btn btn-ghost btn-xs"
            @click="showStaPassword = !showStaPassword"
            :title="showStaPassword ? 'Hide password' : 'Show password'"
          >
            {{ showStaPassword ? 'Hide' : 'Show' }}
          </button>
        </label>
        <p class="label-text-alt text-xs">
          {{ staPasswordStored
            ? 'A password is saved on the device. Leave blank to keep it.'
            : 'Password for that network. Leave blank if it is open.' }}
        </p>
      </fieldset>

      <!-- Reconnect button (USB only) -->
      <div v-if="canReconnect" class="flex items-center gap-3 mt-2 mb-4">
        <button
          type="button"
          class="btn btn-sm btn-primary"
          :disabled="reconnecting"
          @click="reconnect"
        >
          Reconnect
        </button>
        <span v-if="reconnecting" class="text-sm text-base-content/60">Connecting…</span>
        <span v-else-if="reconnectResult === 'failed'" class="text-sm text-error">Could not connect. Check the SSID and password.</span>
        <span v-else-if="reconnectResult === 'error'" class="text-sm text-error">Reconnect failed.</span>
      </div>

      <!-- Connection timeout -->
      <fieldset class="fieldset mb-2">
        <legend class="fieldset-legend">Connection timeout (s)</legend>
          <input
            type="number"
            :value="settings.wifi_connect_timeout_s ?? 10"
            @input="settings.wifi_connect_timeout_s = Number($event.target.value)"
            min="5"
            max="60"
            class="input input-bordered"
          />
        <p class="label-text-alt text-xs">How long to wait when joining your WiFi network before falling back to broadcast mode (5–60 seconds).</p>
        <div v-if="timeoutInvalid" role="alert" class="alert alert-error mb-3">
          <span>Connection timeout must be between 5 and 60 seconds.</span>
        </div>
      </fieldset>
    </div>

    <div v-if="!section || section === 'broadcast'">

      <!-- ── Access point ──────────────────────────────────────────── -->
      <h3 v-if="!section" class="text-xl font-semibold mt-4 pt-2 pb-2">Broadcast mode</h3>

      <p v-if="!section" class="text-gray-500 dark:text-gray-100/50 mb-4">
        In broadcast mode Midulate creates its own WiFi network for you to join.
        This is useful for initial setup, or if there is no WiFi network available.
        Do not leave it open without a password.
        When you join it, use the built-in editor on the device.
      </p>

      <!-- AP Network Name -->
      <fieldset class="fieldset mt-4 mb-2">
        <legend class="fieldset-legend">Network Name</legend>
          <input
            type="text"
            :value="settings.wifi_ap_ssid ?? ''"
            @input="setStrField('wifi_ap_ssid', $event.target.value)"
            min="5"
            max="60"
            class="input input-bordered"
          />
        <p class="label-text-alt text-xs">The name Midulate broadcasts. Leave blank and it uses its own, ending in four characters unique to your pedal.</p>
      </fieldset>

      <!-- AP Password -->
      <fieldset class="fieldset mb-2">
        <legend class="fieldset-legend">Password</legend>
        <label class="input">
          <input
            :type="showApPassword ? 'text' : 'password'"
            :value="settings.wifi_ap_password ?? ''"
            @input="setStrField('wifi_ap_password', $event.target.value)"
            placeholder="Leave blank for open network"
            :maxlength="apPasswordMaxLength"
            class="input input-bordered"
          />
          <button
            type="button"
            class="btn btn-ghost btn-xs"
            @click="showApPassword = !showApPassword"
            :title="showApPassword ? 'Hide password' : 'Show password'"
          >
            {{ showApPassword ? 'Hide' : 'Show' }}
          </button>
        </label>
        <p class="label-text-alt text-xs">
          Broadcast mode password. Leave blank for an open network, or use {{ apPasswordMinLength }}–{{ apPasswordMaxLength }} characters.
        </p>
        <div v-if="apPasswordInvalid" role="alert" class="alert alert-error mb-3">
          <span>The broadcast mode password must be {{ apPasswordMinLength }}–{{ apPasswordMaxLength }} characters, or blank for an open network. Midulate's network will not start otherwise.</span>
        </div>
      </fieldset>

      <!-- Disable broadcast mode (config key wifi_ap_disabled) -->
      <fieldset class="fieldset mb-2">
        <legend class="fieldset-legend">Disable broadcast mode</legend>
          <input
            type="checkbox"
            v-model="settings.wifi_ap_disabled"
            class="checkbox"
          />
        <p class="label-text-alt text-xs">If checked, Midulate will not create its own network when it cannot join yours.</p>
      </fieldset>

      <!-- ── Validation warnings ───────────────────────────────────── -->
      <div v-if="noWifiWarning" role="alert" class="alert alert-warning mb-3">
        Midulate will have no WiFi at all. Either give it a network to join, or allow broadcast mode.
      </div>
    </div>
  </div>
</div>
</template>
